import { Form, FormTextInput, StandardFormButtons } from '@components/form';
import { useEditMcpServerMutation, type McpServer } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { convertMcpConfig, toBackendFormat, type ParsedMcpServer } from '@utils/mcp-config';
import posthog from 'posthog-js';
import { useState } from 'react';
import * as yup from 'yup';
import { McpServerConfigField } from './McpServerConfigField';
import { McpServerDescriptionField } from './McpServerDescriptionField';
import { mcpServerConfigSchema, mcpServerDescriptionSchema, mcpServerNameSchema } from './utils';

const validationSchema = yup.object({
  name: mcpServerNameSchema,
  description: mcpServerDescriptionSchema,
  config: mcpServerConfigSchema,
});

type EditMcpServerConfigModalProps = {
  server: McpServer;
  workspaceId?: string;
  onEdit?: VoidFunction;
  onClose: () => void;
};

export const EditMcpServerConfigModal = ({
  server,
  workspaceId,
  onEdit,
  onClose,
}: EditMcpServerConfigModalProps) => {
  const { organizationId } = useOrganizationContext();
  const [editMcpServer] = useEditMcpServerMutation();

  const [formError, setFormError] = useState<string | null>(null);

  const handleSave = async ({
    name,
    config,
    description,
  }: {
    name: string;
    config: ParsedMcpServer[];
    description?: string;
  }) => {
    posthog.capture('agent-studio:user_edit-mcp-server_submitted', {
      mcpServerId: server.mcpServerId,
    });
    await editMcpServer({
      variables: {
        mcpServerId: server.mcpServerId,
        name,
        config: toBackendFormat(config[0]),
        description,
      },
      onCompleted: () => {
        posthog.capture('agent-studio:user_edit-mcp-server_completed', {
          mcpServerId: server.mcpServerId,
        });
        onEdit?.();
        onClose();
      },
      onError: (error) => {
        posthog.capture('agent-studio:user_edit-mcp-server_failed', {
          mcpServerId: server.mcpServerId,
          error: (error as Error)?.message || 'An error occurred during edit',
        });
        setFormError(error.message);
      },
    });
  };

  return (
    <Form
      initialValues={{
        name: server.name || '',
        config: convertMcpConfig(server.config).servers || [],
        description: server.description || '',
      }}
      onSubmit={handleSave}
      validationSchema={validationSchema}
    >
      <Stack sx={{ width: '500px' }}>
        <FormTextInput name="name" label="Name" />
        <McpServerDescriptionField name="description" />
        <McpServerConfigField
          name="config"
          organizationId={organizationId}
          workspaceId={workspaceId}
        />
        <StandardFormButtons onClose={onClose} formError={formError} submitLabel="Save" />
      </Stack>
    </Form>
  );
};
